/**
 * ⚠️ FORKED COMPONENT — DO NOT MERGE BACK ⚠️
 *
 * FORKED FROM: src/pages/SearchLoadsPageV2.tsx
 *
 * LR DIRECT VERSION
 * This file is intentionally forked to keep LR Direct fully isolated from the main Search Loads system.
 *
 * RULES:
 * - Do NOT import or reuse logic from Search Loads
 * - Do NOT connect to `loads` table
 * - Only use `direct_loads`, `direct_load_stops`, `direct_bids`
 * - Changes here must NOT affect Search Loads
 *
 * If you need shared behavior, duplicate — do not abstract.
 */
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBillingMode } from "@/hooks/useBillingMode";
import { useLRDirectGate } from "@/contexts/LRDirectGateContext";
import { markLRDirectLoadsRead } from "@/hooks/useUnreadLRDirectLoads";
import { Button } from "@/components/ui/button";
import { Crown, X, Sparkles, Filter } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
  ActiveSearchTabs,
  createBlankTab,
  DEFAULT_FILTERS,
  type SearchTab,
  type SearchTabFilters,
} from "@/components/search/ActiveSearchTabs";
import { DirectSearchFilterBar, type DirectFilterParams } from "@/components/lr-direct/DirectSearchFilterBar";
import { DirectResultsTable, type DirectLoad, type DirectSortOption } from "@/components/lr-direct/DirectResultsTable";
import { biddingOpenOrFilter, isBiddingOpen } from "@/lib/lrDirect/directLoadStatus";

/* ─── Local equipment family expansion (forked — do not share with Search Loads) ─── */
const LRD_EQUIPMENT_FAMILIES: Record<string, string[]> = {
  van: [
    'van', 'van_air_ride', 'van_conestoga', 'van_double', 'van_hazmat',
    'van_hotshot', 'van_insulated', 'van_intermodal', 'van_lift_gate',
    'van_logistics', 'van_open_top', 'van_roller_bed', 'van_triple',
    'van_vented', 'van_blanket_wrap', 'van_with_curtains', 'van_pallet_exchange',
    'van_with_team',
    'van_or_flatbed', 'van_or_flatbed_tarps', 'van_or_reefer',
    'insulated_van_or_reefer', 'reefer_or_vented_van', 'flatbed_van_reefer',
  ],
  flatbed: [
    'flatbed', 'flatbed_air_ride', 'flatbed_conestoga', 'flatbed_double',
    'flatbed_hazmat', 'flatbed_hotshot', 'flatbed_maxi', 'flatbed_or_step_deck',
    'flatbed_over_dimension', 'flatbed_with_chains', 'flatbed_with_sides',
    'flatbed_with_tarps', 'flatbed_with_team', 'flatbed_pallet_exchange',
    'van_or_flatbed', 'van_or_flatbed_tarps', 'flatbed_van_reefer',
  ],
  reefer: [
    'reefer', 'reefer_air_ride', 'reefer_double', 'reefer_hazmat',
    'reefer_intermodal', 'reefer_logistics', 'reefer_with_team',
    'reefer_pallet_exchange',
    'reefer_or_vented_van', 'van_or_reefer', 'insulated_van_or_reefer',
    'flatbed_van_reefer',
  ],
};
const LRD_EQUIPMENT_COMBOS = new Set<string>([
  'van_or_reefer', 'van_or_flatbed', 'van_or_flatbed_tarps',
  'insulated_van_or_reefer', 'reefer_or_vented_van', 'flatbed_van_reefer',
]);
function lrdExpandEquipmentTypes(selected: string[]): string[] {
  const expanded = new Set<string>();
  for (const eq of selected) {
    if (LRD_EQUIPMENT_FAMILIES[eq]) {
      for (const m of LRD_EQUIPMENT_FAMILIES[eq]) expanded.add(m);
    } else if (LRD_EQUIPMENT_COMBOS.has(eq)) {
      for (const [, members] of Object.entries(LRD_EQUIPMENT_FAMILIES)) {
        if (members.includes(eq)) for (const m of members) expanded.add(m);
      }
      expanded.add(eq);
    } else {
      expanded.add(eq);
    }
  }
  return Array.from(expanded);
}

/* ─── localStorage helpers ─────────────────────────────────────────── */
const STORAGE_KEY = "lr-direct-search-tabs";
/** One-time migration: tabs persisted while the default loadType was "full"
 *  silently hid every non-FTL load (LTL / Partial / contract). Reset those to
 *  "both" once, then never touch a broker's explicit choice again. */
const LOADTYPE_MIGRATION_KEY = "lr-direct-loadtype-default-migrated-v1";

function loadTabsFromStorage(): { tabs: SearchTab[]; activeTabId: string } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.tabs) && parsed.tabs.length > 0) {
        let tabs: SearchTab[] = parsed.tabs;
        if (!localStorage.getItem(LOADTYPE_MIGRATION_KEY)) {
          tabs = tabs.map((t) =>
            t?.filters?.loadType === "full"
              ? { ...t, filters: { ...t.filters, loadType: "both" as const } }
              : t,
          );
          localStorage.setItem(LOADTYPE_MIGRATION_KEY, "1");
        }
        return { tabs, activeTabId: parsed.activeTabId || tabs[0].id };
      }
    }
    localStorage.setItem(LOADTYPE_MIGRATION_KEY, "1");
  } catch { /* ignore */ }
  return { tabs: [], activeTabId: "" };
}

function saveTabsToStorage(tabs: SearchTab[], activeTabId: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeTabId }));
  } catch { /* ignore */ }
}

/* ─── Build tab label from filters ─────────────────────────────────── */
function buildLabel(filters: SearchTabFilters): string {
  const o = filters.origin?.trim();
  const d = filters.destination?.trim();
  if (o && d) return `${o} → ${d}`;
  if (o) return `${o} → Anywhere`;
  if (d) return `Anywhere → ${d}`;
  return "New Search";
}

/* ═══════════════════════════════════════════════════════════════════ */
export default function LRDirectBoardPage() {
  usePageMeta({
    title: "LR Direct — Browse Direct Loads | LaneRunner",
    description: "Browse direct shipper loads on LR Direct.",
  });

  // Mark private-load notifications read whenever a broker lands on the board.
  const { user: _brdUser, profile, isAdmin, isDemoUser } = useAuth();
  const { hasLRDirectFullAccess, hasPrivateNetworkAccess, isLoading: billingLoading } = useBillingMode();
  const { openGate } = useLRDirectGate();
  useEffect(() => {
    if (_brdUser?.id) markLRDirectLoadsRead(_brdUser.id).catch(() => { /* noop */ });
  }, [_brdUser?.id]);

  // Upgrade banner — broker-only, hidden for shippers / admins / demo / full-access.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const isBroker = profile?.user_type === "broker" || profile?.user_type === "broker_carrier";
  const showUpgradeBanner =
    isBroker && !isAdmin && !isDemoUser && !billingLoading && !hasLRDirectFullAccess && !bannerDismissed;
  const bannerText = hasPrivateNetworkAccess
    ? "Private-network access active. Upgrade to Full Access to bid on open marketplace too."
    : "You're browsing LR Direct. Upgrade to Full Access ($99.99/mo) to start bidding.";



  // ── Tab state (persisted) ──
  const [tabs, setTabs] = useState<SearchTab[]>(() => loadTabsFromStorage().tabs);
  const [activeTabId, setActiveTabId] = useState<string>(() => loadTabsFromStorage().activeTabId);
  const LR_DIRECT_DEFAULTS: SearchTabFilters = { ...DEFAULT_FILTERS, equipmentTypes: [], loadType: "both", loadNature: "all" };
  const pendingFiltersRef = useRef<SearchTabFilters>({ ...LR_DIRECT_DEFAULTS });

  // ── Result cache ──
  const resultsCache = useRef<Map<string, DirectLoad[]>>(new Map());

  // ── Per-tab loading ──
  const [searchingTabs, setSearchingTabs] = useState<Record<string, boolean>>({});
  const autoSearchedTabs = useRef<Set<string>>(new Set());
  const [anyLoadsExist, setAnyLoadsExist] = useState(false);

  // Persist tabs
  useEffect(() => {
    saveTabsToStorage(tabs, activeTabId);
  }, [tabs, activeTabId]);

  // ── Derived ──
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const cachedResults = activeTab ? resultsCache.current.get(activeTab.id) : undefined;
  const isSearching = activeTabId ? !!searchingTabs[activeTabId] : false;
  const currentFilters = activeTab?.filters ?? pendingFiltersRef.current;
  const filterBarKey = activeTabId || "__no-tab__";

  // ── Global empty-state context: are there any loads on the board at all? ──
  useEffect(() => {
    if (!_brdUser) return;
    let ignore = false;
    const check = async () => {
      const { count, error } = await supabase
        .from("direct_loads" as any)
        .select("*", { count: "exact", head: true })
        .eq("status", "posted")
        .or(biddingOpenOrFilter());
      if (ignore) return;
      setAnyLoadsExist((count ?? 0) > 0 && !error);
    };
    check();
    return () => { ignore = true; };
  }, [_brdUser?.id]);

  // ── Refs ──
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // ── Update tab helper ──
  const updateTab = useCallback((tabId: string, patch: Partial<SearchTab>) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, ...patch } : t)));
  }, []);

  // ── Add tab with overflow ──
  const addTabWithOverflow = useCallback((newTab: SearchTab) => {
    setTabs((prev) => {
      if (prev.length >= 10) {
        const oldest = prev[0];
        resultsCache.current.delete(oldest.id);
        return [...prev.slice(1), newTab];
      }
      return [...prev, newTab];
    });
    setActiveTabId(newTab.id);
  }, []);

  // ── Filter change ──
  const handleFilterChange = useCallback((filters: SearchTabFilters) => {
    const tabId = activeTabIdRef.current;
    if (tabId) {
      updateTab(tabId, { filters });
    } else {
      pendingFiltersRef.current = filters;
    }
  }, [updateTab]);

  // ── Auto-load all posted loads on initial mount (no search required) ──
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (initialLoadDone.current) return;
    if (tabs.length > 0) { initialLoadDone.current = true; return; }
    initialLoadDone.current = true;
    // Pre-mark the tab that will be created by handleSearch to prevent the
    // tab-switch useEffect from re-triggering the same search before results arrive.
    const premarkId = `lr-direct-initial-${Date.now()}`;
    autoSearchedTabs.current.add(premarkId);
    handleSearch({
      origin: "",
      destination: "",
      equipmentTypes: [],
      loadType: "both",
      loadNature: "all",
      minLength: "",
      minWeight: "",
      pickupDate: null,
    });
  }, []);

  // ── Auto-search on tab switch ──
  useEffect(() => {
    if (!activeTab || !activeTab.hasSearched) return;
    if (resultsCache.current.has(activeTab.id)) return;
    if (autoSearchedTabs.current.has(activeTab.id)) return;
    autoSearchedTabs.current.add(activeTab.id);
    handleSearch({
      origin: activeTab.filters.origin,
      destination: activeTab.filters.destination,
      equipmentTypes: activeTab.filters.equipmentTypes,
      loadType: (activeTab.filters.loadType as any) || "both",
      loadNature: (activeTab.filters.loadNature as any) || "all",
      minLength: activeTab.filters.minLength || "",
      minWeight: activeTab.filters.minWeight || "",
      pickupDate: activeTab.filters.pickupDate || null,
    });
  }, [activeTabId]);

  // ── Search ──
  const handleSearch = useCallback(async (params: DirectFilterParams) => {
    const newFilters: SearchTabFilters = {
      ...DEFAULT_FILTERS,
      origin: params.origin,
      destination: params.destination,
      equipmentTypes: params.equipmentTypes,
      loadType: params.loadType,
      minLength: params.minLength,
      minWeight: params.minWeight,
      pickupDate: params.pickupDate,
      sortBy: (tabsRef.current.find((t) => t.id === activeTabIdRef.current))?.filters.sortBy ?? "age_newest",
    };
    const label = buildLabel(newFilters);

    const currentTab = tabsRef.current.find((t) => t.id === activeTabIdRef.current);
    let targetTabId: string;
    if (!currentTab) {
      const newTab: SearchTab = {
        id: crypto.randomUUID(),
        label,
        filters: newFilters,
        hasSearched: true,
      };
      targetTabId = newTab.id;
      addTabWithOverflow(newTab);
    } else {
      targetTabId = currentTab.id;
      updateTab(currentTab.id, { filters: newFilters, label, hasSearched: true });
    }

    autoSearchedTabs.current.delete(targetTabId);
    setSearchingTabs(prev => ({ ...prev, [targetTabId]: true }));

    try {
      let query = supabase
        .from("direct_loads" as any)
        .select("id, origin_city, origin_state, destination_city, destination_state, equipment_type, pickup_date, delivery_date, commodity, weight_lbs, bid_deadline, max_bidders, priority_tag, total_miles, total_miles_source, load_type, load_nature, posted_at, length_ft, target_rate_min, target_rate_max, shipper_notes_to_brokers, special_instructions, reference_number, appointment_type, handling_requirements, visibility_mode, private_network_id, post_type, status, brief_audio_path, brief_duration_seconds, atlas_analysis")
        .eq("status", "posted")
        // Deadline guard: a row can still read `posted` for a few minutes
        // after its deadline (expiry sweeps on a schedule) — never show it.
        .or(biddingOpenOrFilter())
        .order("posted_at", { ascending: false });

      if (params.equipmentTypes.length > 0) {
        query = query.in("equipment_type", lrdExpandEquipmentTypes(params.equipmentTypes));
      }

      // Load type filter — map UI values to DB values
      if (params.loadType === "full") {
        query = query.eq("load_type", "FTL");
      } else if (params.loadType === "partial") {
        query = query.in("load_type", ["LTL", "Partial"]);
      } else if (params.loadType === "other") {
        query = query.not("load_type", "in", '("FTL","LTL","Partial")');
      }

      // Load nature filter (spot / dedicated / contract)
      if (params.loadNature && params.loadNature !== "all") {
        query = query.eq("load_nature", params.loadNature);
      }

      // Pickup date filter
      if (params.pickupDate) {
        query = query.gte("pickup_date", params.pickupDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      let results = ((data as unknown as DirectLoad[]) || []).filter((l) => isBiddingOpen(l));

      // Client-side text filter for origin/destination
      const oq = params.origin.toLowerCase().trim();
      const dq = params.destination.toLowerCase().trim();
      if (oq || dq) {
        results = results.filter((load) => {
          const oMatch = !oq || `${load.origin_city}, ${load.origin_state}`.toLowerCase().includes(oq) || load.origin_city.toLowerCase().includes(oq) || load.origin_state.toLowerCase().includes(oq);
          const dMatch = !dq || `${load.destination_city}, ${load.destination_state}`.toLowerCase().includes(dq) || load.destination_city.toLowerCase().includes(dq) || load.destination_state.toLowerCase().includes(dq);
          return oMatch && dMatch;
        });
      }

      // Client-side weight filter
      if (params.minWeight) {
        const minW = parseFloat(params.minWeight);
        if (!isNaN(minW)) {
          results = results.filter((load) => (load.weight_lbs ?? 0) >= minW);
        }
      }

      // Client-side length filter
      if (params.minLength) {
        const minL = parseFloat(params.minLength);
        if (!isNaN(minL)) {
          results = results.filter((load) => ((load as any).length_ft ?? 0) >= minL);
        }
      }

      resultsCache.current.set(targetTabId, results);
    } catch (err) {
      console.error("LR Direct search error:", err);
      resultsCache.current.set(targetTabId, []);
    } finally {
      setSearchingTabs(prev => ({ ...prev, [targetTabId]: false }));
    }
  }, [updateTab, addTabWithOverflow]);

  // ── Tab actions ──
  const handleAddTab = useCallback(() => {
    const tab = createBlankTab();
    tab.filters = { ...tab.filters, loadType: "both" };
    addTabWithOverflow(tab);
  }, [addTabWithOverflow]);

  const handleCloseTab = useCallback((tabId: string) => {
    autoSearchedTabs.current.delete(tabId);
    setSearchingTabs(prev => { const n = { ...prev }; delete n[tabId]; return n; });

    if (tabs.length <= 1) {
      resultsCache.current.delete(tabId);
      pendingFiltersRef.current = { ...LR_DIRECT_DEFAULTS };
      setTabs([]);
      setActiveTabId("");
      return;
    }
    const idx = tabs.findIndex((t) => t.id === tabId);
    const newTabs = tabs.filter((t) => t.id !== tabId);
    resultsCache.current.delete(tabId);

    if (activeTabId === tabId) {
      const newActive = newTabs[Math.max(0, idx - 1)];
      setActiveTabId(newActive.id);
    }
    setTabs(newTabs);
  }, [tabs, activeTabId]);

  const handleSelectTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  // ── Realtime live updates (LR Direct only) ──
  // Force re-render when we mutate the results cache (which is a ref).
  const [, setLiveTick] = useState(0);
  const bumpLive = useCallback(() => setLiveTick((n) => n + 1), []);

  const DIRECT_LOAD_COLS =
    "id, origin_city, origin_state, destination_city, destination_state, equipment_type, pickup_date, delivery_date, commodity, weight_lbs, bid_deadline, max_bidders, priority_tag, total_miles, total_miles_source, load_type, load_nature, posted_at, length_ft, target_rate_min, target_rate_max, shipper_notes_to_brokers, special_instructions, reference_number, appointment_type, handling_requirements, visibility_mode, private_network_id, post_type, status, brief_audio_path, brief_duration_seconds, atlas_analysis";

  const rowMatchesTabFilters = useCallback((row: DirectLoad, f: SearchTabFilters): boolean => {
    if (f.equipmentTypes?.length) {
      const expanded = lrdExpandEquipmentTypes(f.equipmentTypes);
      if (!expanded.includes(row.equipment_type)) return false;
    }
    if (f.loadType === "full" && (row as any).load_type !== "FTL") return false;
    if (f.loadType === "partial" && !["LTL", "Partial"].includes((row as any).load_type ?? "")) return false;
    if (f.loadType === "other" && ["FTL", "LTL", "Partial"].includes((row as any).load_type ?? "")) return false;
    if (f.loadNature && f.loadNature !== "all" && ((row as any).load_nature ?? "spot") !== f.loadNature) return false;
    if (f.pickupDate && (!row.pickup_date || row.pickup_date < f.pickupDate)) return false;
    if (f.minWeight) {
      const m = parseFloat(f.minWeight);
      if (!isNaN(m) && ((row as any).weight_lbs ?? 0) < m) return false;
    }
    if (f.minLength) {
      const m = parseFloat(f.minLength);
      if (!isNaN(m) && ((row as any).length_ft ?? 0) < m) return false;
    }
    const oq = f.origin?.toLowerCase().trim();
    const dq = f.destination?.toLowerCase().trim();
    if (oq) {
      const ok = `${row.origin_city}, ${row.origin_state}`.toLowerCase().includes(oq) ||
        row.origin_city.toLowerCase().includes(oq) ||
        row.origin_state.toLowerCase().includes(oq);
      if (!ok) return false;
    }
    if (dq) {
      const ok = `${row.destination_city}, ${row.destination_state}`.toLowerCase().includes(dq) ||
        row.destination_city.toLowerCase().includes(dq) ||
        row.destination_state.toLowerCase().includes(dq);
      if (!ok) return false;
    }
    return true;
  }, []);

  const removeLoadFromAllTabs = useCallback((loadId: string) => {
    let changed = false;
    for (const tab of tabsRef.current) {
      const cache = resultsCache.current.get(tab.id);
      if (!cache) continue;
      const filtered = cache.filter((l) => l.id !== loadId);
      if (filtered.length !== cache.length) {
        resultsCache.current.set(tab.id, filtered);
        changed = true;
      }
    }
    if (changed) bumpLive();
  }, [bumpLive]);

  const mergeLoadIntoAllTabs = useCallback(async (loadId: string) => {
    try {
      const { data, error } = await supabase
        .from("direct_loads" as any)
        .select(DIRECT_LOAD_COLS)
        .eq("id", loadId)
        .eq("status", "posted")
        .maybeSingle();
      if (error || !data) return;
      const row = data as unknown as DirectLoad;
      if (!isBiddingOpen(row)) return;
      let changed = false;
      for (const tab of tabsRef.current) {
        const cache = resultsCache.current.get(tab.id);
        if (!cache) continue; // tab hasn't loaded results yet — nothing to merge into
        if (cache.some((l) => l.id === row.id)) continue; // dedupe
        if (!rowMatchesTabFilters(row, tab.filters)) continue;
        // posted_at desc ordering — newest at top
        resultsCache.current.set(tab.id, [row, ...cache]);
        changed = true;
      }
      if (changed) bumpLive();
    } catch {
      /* swallow — non-fatal */
    }
  }, [rowMatchesTabFilters, bumpLive]);

  const resyncActiveTab = useCallback(() => {
    const active = tabsRef.current.find((t) => t.id === activeTabIdRef.current);
    if (!active?.hasSearched) return;
    handleSearch({
      origin: active.filters.origin,
      destination: active.filters.destination,
      equipmentTypes: active.filters.equipmentTypes,
      loadType: active.filters.loadType,
      loadNature: active.filters.loadNature ?? "all",
      minLength: active.filters.minLength,
      minWeight: active.filters.minWeight,
      pickupDate: active.filters.pickupDate,
    });
  }, [handleSearch]);

  useEffect(() => {
    const channel = supabase
      .channel("lr-direct-board-live")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "direct_loads" },
        (payload) => {
          const newRow: any = payload.new;
          if (!newRow?.id) return;
          if (!isBiddingOpen(newRow)) return;
          if (newRow.post_type === "quote" && postTypeFilterRef.current !== "quote") {
            setNewQuoteCount((n) => n + 1);
          }
          mergeLoadIntoAllTabs(newRow.id);
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "direct_loads" },
        (payload) => {
          const newRow: any = payload.new;
          const oldRow: any = payload.old;
          if (!newRow?.id) return;
          // Became eligible
          if (isBiddingOpen(newRow) && !isBiddingOpen(oldRow)) {
            mergeLoadIntoAllTabs(newRow.id);
            return;
          }
          // No longer eligible (closed, or deadline now passed) — drop it
          if (!isBiddingOpen(newRow)) {
            removeLoadFromAllTabs(newRow.id);
          }
        }
      )
      .subscribe((status) => {
        // Self-heal: rerun the active tab's search on (re)connect so rows
        // that changed during a CHANNEL_ERROR gap resync.
        if (status === "SUBSCRIBED") resyncActiveTab();
      });

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; resyncActiveTab(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [mergeLoadIntoAllTabs, removeLoadFromAllTabs, resyncActiveTab]);


  // ── Display state ──
  const allLoads = cachedResults ?? [];
  const hasSearched = activeTab?.hasSearched ?? false;
  const currentSortBy = (activeTab?.filters.sortBy ?? "age_newest") as DirectSortOption;

  // ── Visibility segmented control (UI-only; filter feature flag stays off) ──
  const [visibilityFilter, setVisibilityFilter] = useState<"all" | "private" | "open">("all");
  const [postTypeFilter, setPostTypeFilter] = useState<"all" | "load" | "quote">("all");
  const [newQuoteCount, setNewQuoteCount] = useState(0);
  const postTypeFilterRef = useRef(postTypeFilter);
  postTypeFilterRef.current = postTypeFilter;
  const counts = useMemo(() => {
    let priv = 0, open = 0, loads = 0, quotes = 0;
    for (const l of allLoads) {
      if ((l as any).visibility_mode === "private_only") priv++; else open++;
      if ((l as any).post_type === "quote") quotes++; else loads++;
    }
    return { all: allLoads.length, priv, open, loads, quotes };
  }, [allLoads]);

  const displayLoads = useMemo(() => {
    let list = allLoads;
    if (visibilityFilter === "private") list = list.filter((l) => (l as any).visibility_mode === "private_only");
    else if (visibilityFilter === "open") list = list.filter((l) => (l as any).visibility_mode !== "private_only");
    if (postTypeFilter === "load") list = list.filter((l) => (l as any).post_type !== "quote");
    else if (postTypeFilter === "quote") list = list.filter((l) => (l as any).post_type === "quote");
    return list;
  }, [allLoads, visibilityFilter, postTypeFilter]);

  const handleSortChange = useCallback((sort: DirectSortOption) => {
    if (!activeTab) return;
    updateTab(activeTab.id, {
      filters: { ...activeTab.filters, sortBy: sort as any },
    });
  }, [activeTab, updateTab]);

  // P3: explicit refresh — clears the cached result for the active tab and re-runs the
  // search with current filters. Useful after a network membership change so newly
  // visible private loads appear without a hard browser refresh.
  const handleRefresh = useCallback(() => {
    const tabId = activeTabIdRef.current;
    if (!tabId) return;
    resultsCache.current.delete(tabId);
    autoSearchedTabs.current.delete(tabId);
    const f = (tabsRef.current.find((t) => t.id === tabId)?.filters) ?? LR_DIRECT_DEFAULTS;
    handleSearch({
      origin: f.origin,
      destination: f.destination,
      equipmentTypes: f.equipmentTypes,
      loadType: (f.loadType as any) || "both",
      loadNature: (f.loadNature as any) || "all",
      minLength: f.minLength || "",
      minWeight: f.minWeight || "",
      pickupDate: f.pickupDate || null,
    });
  }, [handleSearch]);

  const handleClearFilters = useCallback(() => {
    const tabId = activeTabIdRef.current;
    if (tabId) {
      updateTab(tabId, { filters: { ...LR_DIRECT_DEFAULTS } });
    } else {
      pendingFiltersRef.current = { ...LR_DIRECT_DEFAULTS };
    }
    handleSearch({
      origin: "",
      destination: "",
      equipmentTypes: [],
      loadType: "both",
      loadNature: "all",
      minLength: "",
      minWeight: "",
      pickupDate: null,
    });
  }, [updateTab, handleSearch]);

  // Human-readable chips for whatever filters are narrowing the current tab.
  const activeFilterChips = useMemo(() => {
    const f = currentFilters;
    const out: { key: string; label: string }[] = [];
    if (f.origin?.trim()) out.push({ key: "origin", label: `From: ${f.origin.trim()}` });
    if (f.destination?.trim()) out.push({ key: "destination", label: `To: ${f.destination.trim()}` });
    if (f.equipmentTypes?.length) out.push({ key: "equipment", label: `Equipment: ${f.equipmentTypes.length} selected` });
    if (f.loadType === "full") out.push({ key: "loadType", label: "Size: FTL only" });
    if (f.loadType === "partial") out.push({ key: "loadType", label: "Size: LTL / Partial only" });
    if (f.loadType === "other") out.push({ key: "loadType", label: "Size: Other only" });
    if (f.loadNature && f.loadNature !== "all") out.push({ key: "loadNature", label: `Nature: ${f.loadNature}` });
    if (f.minLength) out.push({ key: "minLength", label: `Min length: ${f.minLength} ft` });
    if (f.minWeight) out.push({ key: "minWeight", label: `Min weight: ${f.minWeight} lbs` });
    if (f.pickupDate) out.push({ key: "pickupDate", label: "Pickup date set" });
    return out;
  }, [currentFilters]);

  const hasActiveFilters = useMemo(() => {
    const f = currentFilters;
    const d = LR_DIRECT_DEFAULTS;
    if (f.origin.trim() !== d.origin) return true;
    if (f.destination.trim() !== d.destination) return true;
    if (f.equipmentTypes.length !== d.equipmentTypes.length || f.equipmentTypes.some((v) => !d.equipmentTypes.includes(v))) return true;
    if (f.loadType !== d.loadType) return true;
    if ((f.loadNature ?? "all") !== (d.loadNature ?? "all")) return true;
    if (f.minLength !== d.minLength) return true;
    if (f.minWeight !== d.minWeight) return true;
    if (f.pickupDate !== d.pickupDate) return true;
    return false;
  }, [currentFilters]);

  const segBtn = (key: "all" | "private" | "open", label: string, count: number) => (
    <button
      key={key}
      type="button"
      onClick={() => setVisibilityFilter(key)}
      className={cn(
        "px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-md transition-colors",
        visibilityFilter === key
          ? "bg-brand-teal text-white shadow-sm"
          : "text-muted-foreground hover:bg-muted",
      )}
    >
      {label}
      <span className={cn(
        "ml-1.5 text-[10px] font-medium",
        visibilityFilter === key ? "text-white/80" : "text-muted-foreground/70",
      )}>{count}</span>
    </button>
  );

  return (
    <div className="flex flex-col w-full h-full">
      {/* ═══ Active Search Tabs ═══ */}
      <ActiveSearchTabs
        tabs={tabs}
        activeTabId={activeTabId}
        onSelectTab={handleSelectTab}
        onAddTab={handleAddTab}
        onCloseTab={handleCloseTab}
      />

      {/* ═══ Filter Bar ═══ */}
      <DirectSearchFilterBar
        key={filterBarKey}
        onSearch={handleSearch}
        isSearching={isSearching}
        resultCount={hasSearched ? displayLoads.length : undefined}
        filterValues={currentFilters}
        onFilterChange={handleFilterChange}
      />

      {/* ═══ Upgrade banner (broker, no full access) ═══ */}
      {showUpgradeBanner && (
        <div className="border-b border-amber-300/60 bg-amber-50 px-3 py-2 flex items-center gap-2">
          <Crown className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-[12px] text-foreground/90 flex-1 min-w-0">{bannerText}</span>
          <Button
            type="button"
            size="sm"
            onClick={() => openGate()}
            className="h-7 text-[11px] font-bold uppercase tracking-wide bg-brand-teal text-white hover:bg-brand-teal/90"
          >
            Upgrade
          </Button>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ═══ Visibility + Type segmented controls ═══ */}
      <div className="border-b border-border bg-card px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mr-1 hidden sm:inline">View:</span>
        <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-0.5">
          {segBtn("all", "All", counts.all)}
          {segBtn("private", "Private", counts.priv)}
          {segBtn("open", "Open Market", counts.open)}
        </div>
        <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-0.5">
          {(["all", "load", "quote"] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setPostTypeFilter(key);
                if (key === "quote") setNewQuoteCount(0);
              }}
              className={cn(
                "relative px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-md transition-colors",
                postTypeFilter === key
                  ? (key === "quote" ? "bg-amber-500 text-white shadow-sm" : "bg-brand-teal text-white shadow-sm")
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              {key === "all" ? "All Types" : key === "load" ? "Loads" : "Quotes"}
              <span className={cn(
                "ml-1.5 text-[10px] font-medium",
                postTypeFilter === key ? "text-white/80" : "text-muted-foreground/70",
              )}>{key === "all" ? counts.all : key === "load" ? counts.loads : counts.quotes}</span>
              {key === "quote" && newQuoteCount > 0 && postTypeFilter !== "quote" && (
                <span
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-background animate-pulse"
                  aria-label={`${newQuoteCount} new quote${newQuoteCount === 1 ? "" : "s"}`}
                >
                  +{newQuoteCount}
                </span>
              )}
            </button>
          ))}
        </div>
        {newQuoteCount > 0 && postTypeFilter !== "quote" && (
          <button
            type="button"
            onClick={() => { setPostTypeFilter("quote"); setNewQuoteCount(0); }}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-amber-500 text-white shadow-sm animate-pulse hover:animate-none hover:bg-amber-600 transition-colors"
          >
            <Sparkles className="h-3 w-3" />
            {newQuoteCount} new quote{newQuoteCount === 1 ? "" : "s"}
          </button>
        )}
        {counts.priv > 0 && visibilityFilter !== "open" && (
          <span className="ml-auto text-[11px] text-brand-teal font-medium hidden md:inline">
            Private loads are exclusive to networks you've been invited to.
          </span>
        )}
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isSearching}
          className={cn(
            "text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto",
            (counts.priv > 0 && visibilityFilter !== "open") && "md:ml-0",
          )}
          title="Refresh — picks up new network access and freshly posted loads"
        >
          {isSearching ? "…" : "Refresh"}
        </button>
      </div>

      {/* ═══ Active per-tab filter cue — brokers must never be silently filtered ═══ */}
      {activeFilterChips.length > 0 && (
        <div className="border-b border-amber-500/40 bg-amber-500/10 px-3 py-1.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            <Filter className="h-3 w-3" />
            Filtered view
          </span>
          {activeFilterChips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 rounded-full border border-amber-500/50 bg-background px-2 py-0.5 text-[11px] font-medium text-foreground"
            >
              {c.label}
            </span>
          ))}
          <button
            type="button"
            onClick={handleClearFilters}
            className="ml-auto text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md border border-amber-500/50 bg-background hover:bg-amber-500/10 transition-colors"
          >
            Show all
          </button>
        </div>
      )}



      {/* ═══ Results ═══ */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <DirectResultsTable
          loads={displayLoads}
          isLoading={isSearching}
          sortBy={currentSortBy}
          onSortChange={handleSortChange}
          hasActiveFilters={hasActiveFilters}
          hasAnyLoads={anyLoadsExist}
          onClearFilters={handleClearFilters}
        />
      </div>
    </div>
  );
}
